import type { IPaymentsRoute } from '#/api/v4Service';

/** `'default'` is the placeholder the platform stores on routes that carry no real chain. */
export const hasCryptoNetwork = (route: IPaymentsRoute): boolean =>
  !!route.crypto_network && route.crypto_network.toLowerCase() !== 'default';

export const isSelectable = (route: IPaymentsRoute): boolean =>
  route.is_active === 'on' && route.is_development !== 'on';

const hasBankDetails = (route: IPaymentsRoute): boolean => !!route.fiat_deposit_properties?.length;

/** The route Back Office designates as the fiat leg. Exactly one per currency is expected. */
const isCryptoToFiat = (route: IPaymentsRoute): boolean => route.is_crypto_to_fiat === 'on';

/**
 * The fiat leg is the route flagged `is_crypto_to_fiat`. Exactly one route per currency is meant
 * to carry it; if a second ever appears the first wins, so a misconfiguration shifts the flow to
 * a predictable route instead of whichever one the API happened to return first.
 *
 * The bank-detail fallbacks stay for the case where no route is flagged at all, so a currency
 * configured the old way keeps working rather than losing fiat entirely. They are ordered so that
 * a route dedicated to fiat whose details have not been filled in yet still wins over a crypto
 * network that happens to carry some, or fiat would be priced against that network's fee.
 *
 * Takes only selectable routes — inactive and development ones are filtered out beforehand.
 */
export const selectFiatRouteSource = (selectable: IPaymentsRoute[]): IPaymentsRoute | null =>
  selectable.find(isCryptoToFiat)
    ?? selectable.find((route) => !hasCryptoNetwork(route) && hasBankDetails(route))
    ?? selectable.find(hasBankDetails)
    ?? selectable.find((route) => !hasCryptoNetwork(route))
    ?? null;
